import { useQuery } from "@tanstack/react-query";
import { groomingSharedAgendaApi } from "@/lib/api/grooming-shared-agenda";
import { groomingRootKey, retryTransientOnce } from "@/hooks/queries/use-work-items";

/**
 * The size-free shared agenda (PKG-PM-C, epic PI-SPEC-SW-E291, feature
 * PI-SPEC-SW-1491). Under the grooming root key, so anything that refreshes
 * every grooming read refreshes it too, but not under the batch key: its
 * shape is not a batch's, and it must never be mixed up with the sized read.
 */
export const sharedAgendaKeys = {
  all: [...groomingRootKey, "shared-agenda"] as const,
  agenda: (batchId: string) => [...sharedAgendaKeys.all, batchId] as const,
};

/**
 * How often an open shared agenda page re-reads the agenda (E323, ER-1710):
 * agendas stay editable until the meeting, so a removal, move or add made
 * elsewhere shows within this without a reload.
 */
export const SHARED_AGENDA_REFRESH_MS = 15_000;

/**
 * The agenda behind a "Share agenda" link, for any signed-in person. Re-read
 * every {@link SHARED_AGENDA_REFRESH_MS} while the page is open and on every
 * return to the window (E323).
 */
export function useSharedAgenda(batchId: string | undefined) {
  return useQuery({
    queryKey: sharedAgendaKeys.agenda(batchId ?? ""),
    queryFn: () => groomingSharedAgendaApi.get(batchId ?? ""),
    select: (response) => response.data,
    enabled: Boolean(batchId),
    retry: retryTransientOnce,
    refetchInterval: SHARED_AGENDA_REFRESH_MS,
    refetchOnWindowFocus: "always",
  });
}
